import type { AgentTableJSON, PivotConfig } from "../api/types";
import { TableView } from "./TableView";
import { PivotTable } from "./PivotTable";
import styles from "./MultiTableView.module.css";
import type { TablePaginationProps } from "./TablePagination";

interface Props {
  tables: AgentTableJSON[];
  pivot?: PivotConfig | null;
  pagination?: TablePaginationProps;
}

/**
 * Renders an array of agent-produced tables sequentially. The primary table
 * receives the optional pivot config (matching how the agent attaches a single
 * pivot to the primary result set in {@link AgentResponsePayload}).
 */
export function MultiTableView({ tables, pivot, pagination }: Props) {
  if (tables.length === 0) return null;
  const pivotIndex = pickPrimaryTableIndex(tables);
  return (
    <div className={styles.list}>
      {tables.map((tbl, i) => (
        <div key={i} className={styles.item}>
          {pivot && i === pivotIndex ? (
            <PivotTable
              columns={tbl.columns}
              rows={tbl.rows}
              pivot={pivot}
              pagination={i === pivotIndex ? pagination : undefined}
            />
          ) : (
            <TableView
              columns={tbl.columns}
              rows={tbl.rows}
              pagination={i === pivotIndex ? pagination : undefined}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function pickPrimaryTableIndex(tables: AgentTableJSON[]): number {
  const explicitPrimary = tables.findIndex((table) => table.role === "primary");
  if (explicitPrimary >= 0) return explicitPrimary;
  for (let i = tables.length - 1; i >= 0; i -= 1) {
    if (tables[i].sql?.trim()) return i;
  }
  return tables.length - 1;
}
